import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useTenantContext } from '@/context/TenantContext';

type Role = Database['public']['Enums']['app_role'];

// Normalize legacy role values to canonical app roles 
// Only needed for backward compatibility with old data
const normalizeRole = (role: Role | (string & {})): Role => {
 const roleStr = String(role);
 // Map legacy roles to canonical app_role enum
 switch (roleStr) {
 case 'rep':
 case 'salesman':
 case 'trainer':
 return 'sales_rep' as Role;
 case 'team_lead':
 case 'sales_manager':
 case 'area_manager':
 case 'regional_manager':
 return 'manager' as Role;
 case 'admin':
 return 'tenant_admin' as Role;
 default:
 return role as Role;
 }
};

interface RoleViewContextType {
 currentRole: Role | null;
 viewingAsRole: Role | null;
 setViewingAsRole: (role: Role | null) => void;
 canSwitchRoles: boolean;
 availableRoles: Role[];
 isLoading: boolean;
 error: string | null;
 refreshRole: () => Promise<void>;
}

const RoleViewContext = createContext<RoleViewContextType | undefined>(undefined);

export function RoleViewProvider({ children }: { children: ReactNode }) {
 const { user, loading: authLoading } = useSupabaseAuth();
 const { tenant } = useTenantContext();
 const [currentRole, setCurrentRole] = useState<Role | null>(null);
 const [viewingAsRole, setViewingAsRole] = useState<Role | null>(null);
 const [userId, setUserId] = useState<string | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 const storageKey = useCallback((uid: string | null) => `role_view:selected:${uid ?? 'anonymous'}`, []);

 const resetState = useCallback(() => {
 setCurrentRole(null);
 setViewingAsRole(null);
 setUserId(null);
 }, []);

 const loadRoleForUser = useCallback(async (uid: string | null) => {
 setError(null);
 setIsLoading(true);

 if (!uid) {
 resetState();
 setIsLoading(false);
 return;
 }

 setUserId(uid);

 try {
 // Prefer authoritative role via RPC (accounts for global_admin assignments)
 let effectiveRole: Role | null = null;

 const { data: roleData, error: roleError } = await supabase
 .rpc('get_user_app_role', { _user_id: uid });

 if (roleError) {
 // Fallback: if RPC fails, query directly from user_roles
 console.warn('[RoleView] RPC get_user_app_role failed, using fallback query:', roleError);
 const { data } = await supabase
 .from('user_roles')
 .select('role')
 .eq('user_id', uid)
 .order('created_at', { ascending: false })
 .limit(1)
 .maybeSingle();

 if (data?.role) {
 effectiveRole = normalizeRole(data.role as Role);
 }
 } else if (roleData) {
 effectiveRole = normalizeRole(roleData as unknown as Role);
 }

 // Extra safety: if user has a global admin assignment, treat them as global_admin.
 // This ensures RoleSwitcher is available even if user_roles isn't updated yet.
 const { data: globalAdminData, error: globalAdminError } = await supabase
 .from('global_admin_assignments')
 .select('id')
 .eq('user_id', uid)
 .maybeSingle();

 if (globalAdminError && globalAdminError.code !== 'PGRST116') {
 console.warn('[RoleView] Error checking global admin status (non-fatal):', globalAdminError);
 }

 if (globalAdminData) {
 effectiveRole = 'global_admin' as Role;
 }

 if (effectiveRole) {
 const actualRole = effectiveRole;
 setCurrentRole(actualRole);
 setError(null);

 // For sales_rep role, ALWAYS view as their actual role (no role switching)
 if (actualRole === 'sales_rep') {
 setViewingAsRole(actualRole);
 // Clear any cached role views for sales reps
 try {
 localStorage.removeItem(storageKey(uid));
 } catch {
 // Intentionally ignore localStorage errors (e.g., in private browsing mode)
 }
 } else if (actualRole === 'global_admin') {
 // Only global_admin can use saved role preferences
 try {
 const saved = localStorage.getItem(storageKey(uid));
 const savedRoleRaw = saved as string | null;
 const savedRole = savedRoleRaw ? normalizeRole(savedRoleRaw as Role) : null;

 if (savedRole && ['global_admin', 'tenant_admin', 'manager', 'sales_rep'].includes(savedRole)) {
 setViewingAsRole(savedRole);
 } else {
 setViewingAsRole(actualRole);
 }
 } catch (storageError) {
 console.warn('localStorage unavailable, using actual role:', storageError);
 setViewingAsRole(actualRole);
 }
 } else {
 // For tenant_admin and manager, always view as their actual role
 setViewingAsRole(actualRole);
 // Clear any cached role views
 try {
 localStorage.removeItem(storageKey(uid));
 } catch {
 // Intentionally ignore localStorage errors
 }
 }
 } else {
 // Optimistic default for new users to unblock UI
 console.warn('⚠️ [RoleView] No role found, using optimistic default: sales_rep');
 const fallbackRole = 'sales_rep' as Role;
 setCurrentRole(fallbackRole);
 setViewingAsRole(fallbackRole);
 setError('Role missing - using default');
 }
 } catch (fetchError) {
 const errorMessage = fetchError instanceof Error ? fetchError.message : 'Failed to fetch user role';
 setError(errorMessage);
 console.error('Error fetching user role:', fetchError);
 setCurrentRole(null);
 setViewingAsRole(null);
 } finally {
 setIsLoading(false);
 }
 }, [resetState, storageKey]);

 useEffect(() => {
 if (authLoading) return;
 loadRoleForUser(user?.id ?? null);
 }, [authLoading, user?.id, tenant?.id, loadRoleForUser]);

 useEffect(() => {
 if (userId && viewingAsRole && !isLoading) {
 try {
 localStorage.setItem(storageKey(userId), viewingAsRole);
 } catch (storageError) {
 console.warn('Failed to persist role selection:', storageError);
 }
 }
 }, [userId, viewingAsRole, isLoading, storageKey]);

 const handleSetViewingAsRole = useCallback((role: Role | null) => {
 if (!role || !currentRole) {
 setViewingAsRole(currentRole);
 return;
 }

 // Only global_admin can switch roles
 if (currentRole !== 'global_admin') {
 setViewingAsRole(currentRole);
 return;
 }

 // Global admin can switch to any of the 4 main roles
 const allowedRoles: Role[] = ['global_admin', 'tenant_admin', 'manager', 'sales_rep'];
 if (allowedRoles.includes(role)) {
 setViewingAsRole(role);
 } else {
 console.warn(`Invalid role for switching: ${role}`);
 setViewingAsRole(currentRole);
 }
 }, [currentRole]);

 const canSwitchRoles = useMemo(() => (
 currentRole === 'global_admin'
 ), [currentRole]);

 const availableRoles = useMemo(() => {
 if (currentRole === 'global_admin') {
 // Global admins can switch between all 4 roles
 return ['global_admin', 'tenant_admin', 'manager', 'sales_rep'] as Role[];
 }
 // All other roles cannot switch
 return [];
 }, [currentRole]);

 const refreshRole = useCallback(async () => {
 await loadRoleForUser(user?.id ?? null);
 }, [loadRoleForUser, user?.id]);

 const contextValue = useMemo(() => ({
 currentRole,
 viewingAsRole,
 setViewingAsRole: handleSetViewingAsRole,
 canSwitchRoles,
 availableRoles,
 isLoading,
 error,
 refreshRole
 }), [currentRole, viewingAsRole, handleSetViewingAsRole, canSwitchRoles, availableRoles, isLoading, error, refreshRole]);

 return (
 <RoleViewContext.Provider value={contextValue}>
 {children}
 </RoleViewContext.Provider>
 );
}

export const useRoleView = () => {
 const context = useContext(RoleViewContext);

 if (context === undefined) {
 console.error('useRoleView called outside RoleViewProvider');
 console.error('Current location:', window.location.pathname);
 console.error('Call stack:', new Error().stack);

 return {
 currentRole: null,
 viewingAsRole: null,
 setViewingAsRole: () => {},
 canSwitchRoles: false,
 availableRoles: [],
 isLoading: true,
 error: 'Context not available',
 refreshRole: async () => {}
 } as RoleViewContextType;
 }

 return context;
};
